<template>
    <DataTable :value="filteredPostbackLog" scrollable :scrollHeight="'80vh'" class="w-full mt-5" :sortField="'approvedAt'"
        :sortOrder="-1" :loading="linksLoading"   filterDisplay="row"
        v-model:filters="filters" >
        <Column header="Date Sent" field="createdAt" :headerStyle="headerStyles" :showFilterMenu="false">        
            <template #body="{data}">  
                <div v-tooltip.top="getUserAgent(data)">
                    <span>{{ getFormattedDate(data.createdAt) }}</span>
                </div>
            </template>
        </Column>
        <Column header="Publisher" filterField="userName" :headerStyle="headerStyles" :showFilterMenu="false">
            <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                    v-model="filterModel.value"
                    @change="filterCallback()"
                    :options="distinctPublishers"
                    placeholder="Any"
                    :maxSelectedLabels="1"
                    :showClear="false"
                >
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option }}</span>
                        </div>
                    </template>
                </MultiSelect>
            </template>
            <template #body="{data}">
                <router-link v-if="data.userId" class="underline" :to="`/users/${data.userId._id}`">
                    {{ data.userId.type === 'Company' ? data.userId.companyName : data.userId.name }}
                </router-link>
            </template>
        </Column>
        <Column header="Event" filterField="eventType" :headerStyle="headerStyles" :showFilterMenu="false">
            <template #filter="{ filterModel, filterCallback }">
                <Select
                    v-model="filterModel.value"
                    :options="eventTypeOptions"
                    placeholder="Select"
                    style="min-width: 10rem"
                    optionLabel="label"
                    optionValue="value"
                    @change="filterCallback()"
                    :showClear="true"
                />
            </template>
            <template #body="slotProps">
                {{ 
                    slotProps.data.eventType === 'cpa' ? 'Qualification' :
                    slotProps.data.eventType === 'ftd' ? 'FTD' :
                    slotProps.data.eventType === 'reg' ? 'Register' :
                    slotProps.data.eventType 
                }}
            </template>
        </Column>
        <Column header="Advertiser/partner"  :headerStyle="headerStyles">
            <template #body="{data}">
                <div>{{ data.advertiserId?.name || data.partner || 'N/A' }}<span v-if="data.market" :class="`fi fi-${data.market.toLowerCase()}`" class="ml-2"></span></div>
            </template>
        </Column>
        <Column header="Reuquested url"  :headerStyle="headerStyles">
            <template #body="slotProps">
                <div>{{ slotProps.data.fullUrl }}</div>
            </template>
        </Column>
        <Column header="Status" field="statusCode" :headerStyle="headerStyles">
            <template #body="slotProps">
                <Tag 
                    :value="slotProps.data.statusCode"
                    :severity="slotProps.data.statusCode === 200 ? 'success' : 'danger'"
                    class="dark:bg-opacity-20"
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup>
import { shallowRef, onMounted, computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import PostbackService from '@/services/PostbackService';
import { headerStyles } from '@/global/headerStyles';
import { getFormattedDate } from '@/utils/dateHelper';
import { useStore } from 'vuex';
import { FilterMatchMode } from '@primevue/core/api';
const store = useStore();
const route = useRoute();
const userId = computed(() => store.getters.userId);
const postbackLog = shallowRef([]);
const linksLoading = shallowRef(true);
const distinctPublishers = shallowRef([]);

const props = defineProps({
  dateRange: {
    type: Array,
    default: null
  }
});

const dateFilterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' }
];

const eventTypeOptions = [
    { label: 'FTD', value: 'ftd' },
    { label: 'Register', value: 'reg' }
];

const dateFilterSelection = ref(null);

const filters = ref({
    userName: { value: null, matchMode: FilterMatchMode.IN },
    eventType: { value: null, matchMode: FilterMatchMode.EQUALS }
});

/**
 * paramN query-string filters. Populated from the URL, applied client-side
 * as exact-match on postback.params[paramKey]. Set by the "drill" button
 * on the Params breakdown view.
 */
const paramFilters = computed(() => {
    const out = {};
    for (const key of Object.keys(route.query)) {
        if (/^param\d+$/.test(key)) out[key] = String(route.query[key]);
    }
    return out;
});

function matchesParamFilters(item) {
    for (const [k, v] of Object.entries(paramFilters.value)) {
        if (String(item.params?.[k] ?? '') !== v) return false;
    }
    return true;
}

const filteredPostbackLog = computed(() => {
    const base = postbackLog.value.filter(matchesParamFilters);
    if (dateFilterSelection.value === 'all' || !dateFilterSelection.value) {
        return base;
    }
    return base.filter(item => {
        const date = new Date(item.createdAt);
        const today = new Date();

        if (dateFilterSelection.value === 'today') {
            return date.getFullYear() === today.getFullYear() &&
                   date.getMonth() === today.getMonth() &&
                   date.getDate() === today.getDate();
        }
        if (dateFilterSelection.value === 'week') {
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startOfWeek.setHours(0,0,0,0);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            endOfWeek.setHours(23,59,59,999);
            return date >= startOfWeek && date <= endOfWeek;
        }
        if (dateFilterSelection.value === 'month') {
            return date.getFullYear() === today.getFullYear() &&
                   date.getMonth() === today.getMonth();
        }
        return true;
    });
});

const getUserAgent = (data) => {
    if (!data.params || typeof data.params !== 'object') return '';
    return Object.entries(data.params)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
}

const getPostbackLog = async (from,to) => {
    try {
        const response = await PostbackService.getPostbackLogAdmin(userId.value, {from, to});
        postbackLog.value = response.data;
    } catch (error) {
        console.error(error);
    }finally{
        linksLoading.value = false;
    }
}

onMounted(async () => {
    console.log('dateRange', props.dateRange);
    await getPostbackLog(props.dateRange[0], props.dateRange[1]);

    // Get distinct usernames
    distinctPublishers.value = [...new Set(postbackLog.value
        .filter(item => item.userName)
        .map(item => item.userName))];
});

// Update the watch to use a getter function for props
watch(() => props.dateRange, async (newDateRange) => {
    console.log('dateRange', newDateRange);
    console.log('length', newDateRange.length);
    if (newDateRange && newDateRange[1] !== null) {
        await getPostbackLog(newDateRange[0], newDateRange[1]);
    }
}, { deep: true });
function paramsToString(params) {
    if (!params || typeof params !== 'object') return '';
    return Object.entries(params)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
}

</script>

<style scoped>

</style>